import {
  buildWeldingPassRowLabels,
  WELDING_PASS_NAME_OPTIONS,
} from '../../../../../constants/weldingPassNames';

export { WELDING_PASS_NAME_OPTIONS };

/** Manual pass row on spool welding step — travel speed and heat input are derived on save/display. */
export type WeldingPassTimeUnit = 'min' | 'sec' | 'minsec';

/** Column label for arc time entry (minutes + seconds encoded as e.g. 8.11 = 8 min 11 sec). */
export const WELDING_PASS_TIME_MIN_SEC_LABEL = 'Time (min/sec)';
export type WeldingPassConfigRow = {
  id: string;
  /** Pass name from {@link WELDING_PASS_NAME_OPTIONS}. */
  passName: string;
  amps: string;
  volts: string;
  time: string;
  timeUnit: WeldingPassTimeUnit;
  lengthIn: string;
  /** User-entered interpass temperature (°C). */
  interpassTempC: string;
};

export type WeldingPassConfigPayloadRow = {
  passNo: number;
  passName: string | null;
  amps: number | null;
  volts: number | null;
  /** Display value as entered (e.g. `8.11` = 8 min 11 sec). */
  time: string | null;
  /** Total arc time in minutes (for calculations / API). */
  arcTimeMinutes: number | null;
  timeUnit: WeldingPassTimeUnit;
  lengthIn: number | null;
  travelSpeedIpm: number | null;
  heatInputKjIn: number | null;
  interpassTempC: number | null;
};

let passRowIdSeq = 0;

function nextPassRowId(): string {
  passRowIdSeq += 1;
  return `pass-${passRowIdSeq}-${Date.now()}`;
}

export function createPassConfigRowsFromModal(
  passName: string,
  numberOfPasses: number
): WeldingPassConfigRow[] {
  return buildWeldingPassRowLabels(passName, numberOfPasses).map((label) => ({
    id: nextPassRowId(),
    passName: label,
    amps: '',
    volts: '',
    time: '',
    timeUnit: 'minsec' as WeldingPassTimeUnit,
    lengthIn: '',
    interpassTempC: '',
  }));
}

function parseNonNegativeNumber(raw: string): number | null {
  const trimmed = String(raw).trim();
  if (!trimmed) return null;
  const n = parseFloat(trimmed);
  return Number.isFinite(n) && n >= 0 ? n : null;
}

function parsePositiveNumber(raw: string): number | null {
  const n = parseFloat(String(raw).trim());
  return Number.isFinite(n) && n > 0 ? n : null;
}

/**
 * Parses arc time as min/sec: `8.11` → 8 min 11 sec, `8:11` → same. Returns total minutes.
 */
export function parseMinSecTimeInput(raw: string): number | null {
  const trimmed = String(raw).trim();
  if (!trimmed) return null;

  if (trimmed.includes(':')) {
    const [minPart, secPart = '0'] = trimmed.split(':');
    const minutes = parseInt(minPart, 10);
    const seconds = parseInt(secPart, 10);
    if (
      !Number.isFinite(minutes) ||
      minutes < 0 ||
      !Number.isFinite(seconds) ||
      seconds < 0 ||
      seconds >= 60
    ) {
      return null;
    }
    const total = minutes + seconds / 60;
    return total > 0 ? total : null;
  }

  const n = parseFloat(trimmed);
  if (!Number.isFinite(n) || n < 0) return null;

  const wholeMinutes = Math.floor(n);
  const fractional = n - wholeMinutes;
  const seconds = Math.round(fractional * 100 + 1e-6);
  if (seconds >= 60) return null;
  const totalMinutes = wholeMinutes + seconds / 60;
  return totalMinutes > 0 ? totalMinutes : null;
}

function arcTimeMinutesFromRow(row: WeldingPassConfigRow): number | null {
  if (row.timeUnit === 'sec') {
    const sec = parsePositiveNumber(row.time);
    return sec != null ? sec / 60 : null;
  }
  if (row.timeUnit === 'min') {
    return parsePositiveNumber(row.time);
  }
  return parseMinSecTimeInput(row.time);
}

/** Travel speed (ipm) = length (in) ÷ arc time (min). Heat input (kJ/in) = (V × A × 60) ÷ (TS × 1000). */
export function computeWeldingPassMetrics(row: WeldingPassConfigRow): {
  travelSpeedIpm: number | null;
  heatInputKjIn: number | null;
} {
  const amps = parsePositiveNumber(row.amps);
  const volts = parsePositiveNumber(row.volts);
  const timeMinutes = arcTimeMinutesFromRow(row);
  const lengthIn = parsePositiveNumber(row.lengthIn);

  if (timeMinutes == null || !lengthIn) {
    return { travelSpeedIpm: null, heatInputKjIn: null };
  }

  const travelSpeedIpm = lengthIn / timeMinutes;

  let heatInputKjIn: number | null = null;
  if (amps != null && volts != null && travelSpeedIpm > 0) {
    heatInputKjIn = (volts * amps * 60) / (travelSpeedIpm * 1000);
  }

  return { travelSpeedIpm, heatInputKjIn };
}

export function formatWeldingPassMetric(value: number | null, fractionDigits = 1): string {
  if (value == null || !Number.isFinite(value)) return '—';
  return value.toFixed(fractionDigits);
}

function readPassField(o: Record<string, unknown>, ...keys: string[]): string {
  for (const key of keys) {
    const v = o[key];
    if (v != null && v !== '') return String(v);
  }
  return '';
}

function normalizePassRowFromApi(item: unknown, index: number): WeldingPassConfigRow {
  const o =
    item && typeof item === 'object' ? (item as Record<string, unknown>) : ({} as Record<string, unknown>);
  const timeUnitRaw = o.timeUnit ?? o.time_unit;
  return {
    id: nextPassRowId(),
    passName: readPassField(o, 'passName', 'pass_name', 'name'),
    amps: readPassField(o, 'amps', 'amperage', 'current'),
    volts: readPassField(o, 'volts', 'voltage'),
    time: readPassField(o, 'time', 'arcTime', 'arc_time'),
    timeUnit:
      timeUnitRaw === 'sec' || timeUnitRaw === 'seconds'
        ? 'sec'
        : timeUnitRaw === 'min' || timeUnitRaw === 'minutes'
          ? 'min'
          : 'minsec',
    lengthIn: readPassField(o, 'lengthIn', 'length_in', 'length'),
    interpassTempC: readPassField(
      o,
      'interpassTempC',
      'interpass_temp_c',
      'interpassTemperature',
      'interpass_temperature'
    ),
  };
}

export function parsePassConfigurationFromPayload(raw: unknown): WeldingPassConfigRow[] {
  if (!Array.isArray(raw) || raw.length === 0) return [];
  return raw.map((item, index) => normalizePassRowFromApi(item, index));
}

export function passConfigRowsToPayload(rows: WeldingPassConfigRow[]): WeldingPassConfigPayloadRow[] {
  return rows.map((row, index) => {
    const amps = parsePositiveNumber(row.amps);
    const volts = parsePositiveNumber(row.volts);
    const timeMinutes = arcTimeMinutesFromRow(row);
    const lengthIn = parsePositiveNumber(row.lengthIn);
    const { travelSpeedIpm, heatInputKjIn } = computeWeldingPassMetrics(row);
    const passName = row.passName.trim();
    const timeDisplay = row.time.trim();
    return {
      passNo: index + 1,
      passName: passName.length > 0 ? passName : null,
      amps,
      volts,
      time: timeDisplay.length > 0 ? timeDisplay : null,
      arcTimeMinutes: timeMinutes,
      timeUnit: row.timeUnit === 'min' || row.timeUnit === 'sec' ? row.timeUnit : 'minsec',
      lengthIn,
      travelSpeedIpm,
      heatInputKjIn,
      interpassTempC: parseNonNegativeNumber(row.interpassTempC),
    };
  });
}

export function getPassConfigurationFromPayload(pl: Record<string, unknown>): unknown {
  return (
    pl.passConfiguration ??
    pl.pass_configuration ??
    pl.passConfig ??
    pl.pass_config ??
    null
  );
}
